import { boundMethod } from 'autobind-decorator';
import isEqual from 'lodash/isEqual';
import unionBy from 'lodash/unionBy';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

export interface WithSelectionInjectedProps<T> {
  selection: {
    /** The selected items */
    selectedItems: Array<T>;

    /**
     * Sets the selected items. Usually used to show pre-selected items.
     *
     * @param {T[]} items - The items to be set
     */
    setSelectedItems: (items: Array<T>) => void;

    /**
     * Select an item
     *
     * @param {T} item - The item to be selected
     */
    selectItem: (item: T) => void;

    /**
     * Deselect an item
     *
     * @param {T} item - The item to be deselected
     */
    deselectItem: (item: T) => void;
  };
}

type WithSelectionNotInjectedProps<TType, TKeys> = Omit<
  TType,
  keyof WithSelectionInjectedProps<TKeys>
>;

type WithSelectionProps<TType, TKeys> = WithSelectionNotInjectedProps<
  TType,
  TKeys
>;

interface WithSelectionState<T> {
  selectedItems: Array<T>;
}

/**
 * An HOC to implement selection logic and provide them as props to the wrapped
 * component
 *
 * @param Component The wrapped component
 * @param uniqueIdentifier The unique field name of the Item type
 */
export const withSelection = <
  TComponent extends WithSelectionInjectedProps<T>,
  T,
>(
  Component: ComponentType<TComponent>,
  uniqueIdentifier = 'name',
) => {
  class WithSelection extends PureComponent<
    WithSelectionProps<TComponent, T>,
    WithSelectionState<T>
  > {
    constructor(props: WithSelectionProps<TComponent, T>) {
      super(props);

      this.state = {
        selectedItems: [],
      };

      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    @boundMethod
    handleSetSelectedItems(items: Array<T>): void {
      this.setState({ selectedItems: items });
    }

    @boundMethod
    handleSelectItem(item: T): void {
      this.setState(({ selectedItems }) => ({
        selectedItems: unionBy(selectedItems, [item], uniqueIdentifier),
      }));
    }

    @boundMethod
    handleDeselectItem(item: T): void {
      this.setState(({ selectedItems }) => ({
        selectedItems: selectedItems.filter(
          (t) => t[uniqueIdentifier] !== item[uniqueIdentifier],
        ),
      }));
    }

    @boundMethod
    getInjectedProps(
      state: Readonly<WithSelectionState<T>>,
    ): WithSelectionInjectedProps<T>['selection'] {
      const { selectedItems } = state;

      return this.memoizedGetInjectedProps(selectedItems);
    }

    memoizedGetInjectedProps(
      selectedItems: Readonly<WithSelectionState<T>>['selectedItems'],
    ): WithSelectionInjectedProps<T>['selection'] {
      return {
        selectedItems: selectedItems,
        setSelectedItems: this.handleSetSelectedItems,
        selectItem: this.handleSelectItem,
        deselectItem: this.handleDeselectItem,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as TComponent)}
          selection={this.getInjectedProps(this.state)}
        />
      );
    }
  }

  return WithSelection;
};
